import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ShieldCheck, Download, Loader2, Gift, PlayCircle, Package } from "lucide-react";
import SiteLayout from "@/components/SiteLayout";
import Reveal from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import ShareBar from "@/components/ShareBar";
import OrgAutocomplete from "@/components/OrgAutocomplete";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [bundleItems, setBundleItems] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState("");
  const [claimOrg, setClaimOrg] = useState("");
  const [claimed, setClaimed] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
        // SEO: redirect old id-based URLs to the canonical slug URL (client-side 301 equivalent)
        if (data.slug && data.slug !== id) {
          navigate(`/academy/${data.slug}`, { replace: true });
        }
        if (data.type === "bundle" && Array.isArray(data.bundle_products) && data.bundle_products.length) {
          const { data: all } = await api.get("/products");
          const byId = Object.fromEntries(all.map((p) => [p.id, p]));
          setBundleItems(data.bundle_products.map((pid) => byId[pid]).filter(Boolean));
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const handleCheckout = async () => {
    setError("");
    setCheckoutLoading(true);

    // FREE product — require org, then claim
    if (product.is_free) {
      if (!user || user === false) {
        navigate(`/register?redirect=/academy/${id}`);
        return;
      }
      if (!claimOrg.trim()) {
        setError("Please enter your organization to claim this free resource.");
        setCheckoutLoading(false);
        return;
      }
      try {
        await api.post("/products/claim", { product_id: product.id, organization: claimOrg.trim() });
        setClaimed(true);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setCheckoutLoading(false);
      }
      return;
    }

    // For memberships, require login (so we can attribute membership)
    if (product.type === "membership" && (!user || user === false)) {
      navigate(`/login?redirect=/academy/${id}`);
      return;
    }

    try {
      const origin = window.location.origin;
      const endpoint = product.type === "membership" ? "/checkout/subscription" : "/checkout/session";
      track("begin_checkout", {
        currency: "USD",
        value: Number(product.price) || 0,
        items: [{ item_id: product.id, item_name: product.name, item_category: product.category, price: Number(product.price) || 0, quantity: 1 }],
      });
      const { data } = await api.post(endpoint, {
        product_id: product.id,
        origin_url: origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e));
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <SiteLayout branch="main">
        <div className="section text-center text-[var(--muted)]">Loading product…</div>
      </SiteLayout>
    );
  }

  if (notFound || !product) {
    return (
      <SiteLayout branch="main">
        <div className="section text-center">
          <h2 className="font-display text-4xl text-[var(--navy)]">Product not found</h2>
          <Link to="/academy" className="text-link mt-8 inline-flex">Back to Academy</Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout branch="main">
      <SEO
        title={product.name}
        description={product.tagline || product.description?.slice(0, 155)}
        path={`/academy/${product.slug || product.id}`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.description,
          category: product.category,
          sku: product.id,
          brand: { "@type": "Organization", name: "Catalyst BH Consulting" },
          offers: {
            "@type": "Offer",
            price: product.price.toFixed(2),
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: `https://catalystbhconsulting.com/academy/${product.slug || product.id}`,
            priceSpecification: product.type === "membership" ? {
              "@type": "UnitPriceSpecification",
              price: product.price.toFixed(2),
              priceCurrency: "USD",
              billingIncrement: 1,
              unitCode: "MON",
              referenceQuantity: {
                "@type": "QuantitativeValue",
                value: Math.round((product.period_days || 30) / 30),
                unitCode: "MON",
              },
            } : undefined,
          },
        }}
      />
      <section className="bg-[var(--light)] py-12 px-[6vw]">
        <Link to="/academy" className="text-link" data-testid="back-to-academy">
          <ArrowLeft size={14} /> Back to Academy
        </Link>
      </section>

      <section className="bg-white px-[6vw] pb-24">
        <div className="grid lg:grid-cols-[1.3fr_0.9fr] gap-16 max-w-[1300px] mx-auto">
          <Reveal>
            <span className="eyebrow-dark">{product.category.toUpperCase()}</span>
            <h1 className="font-display mt-5 text-[clamp(38px,4.5vw,64px)] leading-[1.04] text-[var(--navy)]" data-testid="product-name">
              {product.name}
            </h1>
            <p className="text-[var(--muted)] text-lg mt-5">{product.tagline}</p>
            <div className="prose max-w-none mt-8 text-[var(--ink)] leading-[1.8]">
              <p>{product.description}</p>
            </div>

            <ShareBar
              title={product.name}
              description={product.tagline || product.description || ""}
              url={`https://catalystbhconsulting.com/academy/${product.slug || product.id}`}
              className="mt-8 pb-2 border-b border-[var(--line)]"
            />

            <h3 className="font-display text-2xl text-[var(--navy)] mt-10 mb-4">What's included</h3>
            <ul className="space-y-3">
              {product.features?.map((f) => (
                <li key={f} className="flex gap-3 items-start text-[var(--ink)]">
                  <Check size={18} className="text-[var(--gold)] flex-shrink-0 mt-1" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            {product.type === "bundle" && bundleItems.length > 0 && (
              <div className="mt-12 border-t border-[var(--line)] pt-10" data-testid="bundle-items">
                <span className="eyebrow-dark inline-flex items-center gap-2"><Package size={14} /> BUNDLED PRODUCTS</span>
                <h3 className="font-display text-2xl text-[var(--navy)] mt-3 mb-6">
                  Every item in this bundle unlocks after checkout
                </h3>
                <div className="space-y-4">
                  {bundleItems.map((b) => (
                    <Link
                      key={b.id}
                      to={`/academy/${b.slug || b.id}`}
                      className="flex items-start justify-between gap-4 border border-[var(--line)] hover:border-[var(--navy)] bg-white p-5 transition-colors group"
                      data-testid={`bundle-item-${b.id}`}
                    >
                      <div className="min-w-0">
                        <div className="text-[10px] tracking-widest text-[var(--gold)] font-bold">
                          {b.category?.toUpperCase()}
                        </div>
                        <div className="font-display text-lg text-[var(--navy)] mt-1">{b.name}</div>
                        <div className="text-sm text-[var(--muted)] mt-1">{b.tagline}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-[10px] tracking-widest text-[var(--muted)]">RETAIL</div>
                        <div className="font-display text-lg text-[var(--navy)] line-through opacity-60">
                          ${Number(b.price || 0).toFixed(2).replace(/\.00$/, "")}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-5 text-sm text-[var(--muted)]">
                  <strong className="text-[var(--navy)]">Bundle savings:</strong>{" "}
                  ${(bundleItems.reduce((s, b) => s + Number(b.price || 0), 0) - Number(product.price || 0)).toFixed(0)} off retail.
                </div>
              </div>
            )}
          </Reveal>

          <Reveal>
            <aside
              className="sticky top-32 border border-[var(--line)] bg-[var(--cream)] p-8"
              data-testid="product-buybox"
            >
              <div className="text-[10px] tracking-widest text-[var(--muted)] mb-2">
                {product.is_free ? "FREE · ORGANIZATION REQUIRED"
                  : product.type === "membership" ? "MEMBERSHIP · CANCEL ANYTIME"
                  : "ONE-TIME PURCHASE"}
              </div>
              <div className="flex items-baseline gap-2 mb-6">
                {product.is_free ? (
                  <span className="font-display text-5xl text-[var(--gold)]" data-testid="product-price">FREE</span>
                ) : (
                  <>
                    <span className="font-display text-5xl text-[var(--navy)]" data-testid="product-price">
                      ${product.price.toFixed(2).replace(/\.00$/, "")}
                    </span>
                    {product.type === "membership" && (
                      <span className="text-[var(--muted)] text-sm">
                        {product.period_days === 30 ? "/ month" : "/ year"}
                      </span>
                    )}
                  </>
                )}
              </div>

              {product.is_free && !claimed && user && user !== false && (
                <div className="field mb-4">
                  <label className="field-label">Your organization</label>
                  <OrgAutocomplete value={claimOrg} onChange={setClaimOrg} testId="claim-org" placeholder="e.g. Riverside Behavioral Health" />
                  <p className="text-[10px] text-[var(--muted)] mt-1">Required for free resources so we can track adoption across organizations.</p>
                </div>
              )}

              {claimed ? (
                <div className="bg-green-50 border border-green-200 p-4 text-center" data-testid="claim-success">
                  <Check size={24} className="text-green-600 mx-auto mb-2" />
                  <div className="text-[var(--navy)] font-bold">You have access.</div>
                  <div className="text-xs text-[var(--muted)] mt-1">Find it under "Your library" on your Dashboard.</div>
                  <Link to="/dashboard" className="btn btn-navy w-full mt-4 justify-center">Go to Dashboard</Link>
                </div>
              ) : (
                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="btn btn-gold w-full justify-center"
                  data-testid="buy-button"
                >
                  {checkoutLoading ? (
                    <><Loader2 className="animate-spin mr-2" size={16} /> Working…</>
                  ) : product.is_free ? (
                    <>{user && user !== false ? "Claim Free Access" : "Sign up to claim"} <Gift size={14} className="ml-2" /></>
                  ) : product.type === "membership" ? (
                    "Start Membership"
                  ) : (
                    <>Buy Now <Download size={14} className="ml-2" /></>
                  )}
                </button>
              )}

              {error && (
                <p className="text-red-700 text-xs mt-3" data-testid="checkout-error">{error}</p>
              )}

              {product.type === "course" && user && user !== false && (
                <Link
                  to={`/academy/${product.id}/learn`}
                  className="btn btn-navy w-full justify-center mt-3"
                  data-testid="start-learning"
                >
                  Start Learning <PlayCircle size={14} className="ml-2" />
                </Link>
              )}

              <div className="mt-6 pt-6 border-t border-[var(--line)] text-xs text-[var(--muted)] space-y-2">
                <div className="flex gap-2"><ShieldCheck size={14} className="text-[var(--gold)]" /> Secure Stripe checkout</div>
                <div className="flex gap-2"><Check size={14} className="text-[var(--gold)]" /> Instant access after payment</div>
                {product.type === "membership" && (
                  <div className="flex gap-2"><Check size={14} className="text-[var(--gold)]" /> Renews on your schedule</div>
                )}
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </SiteLayout>
  );
}
